import { useState } from 'react'
import Image from 'next/image'

import { composeClasses } from '@wiggot/utils/src/strings'
import { GoogleIcon } from '@dd3tech/wiggui/GoogleIcon'

import switchIcon from '@/assets/svg/wiggot-switch-icon.svg'

const CLASS_SWITCH_WRAPPER = 'aspect-[8/5] relative inline-flex group'
const CLASS_ROUNDED_CENTER =
  'rounded-full absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]'
const CLASS_SWITCH_BG = `h-[70%] w-[80%] ${CLASS_ROUNDED_CENTER}`
const CLASS_SWITCH_OUTER_SLIDER = `rounded-full absolute top-[50%] translate-y-[-50%] aspect-square h-[100%] transition-all`
const CLASS_SWITCH_INNER_SIZE = 'h-[50%] w-[50%]'
const CLASS_SWITCH_INNER_SLIDER = `${CLASS_SWITCH_INNER_SIZE} ${CLASS_ROUNDED_CENTER}`

// CLASS_PIVOT_RIGHT CALCULATION
// Tailwind doesn't support dynamic class generation so I leave it as a comment
// const ASPECT_RATIO_X = `8`
// const ASPECT_RATIO_Y = `5`
// const OUTER_SLIDER_HEIGHT = `1`
// const LEFT_PERCENTAGE = Math.round(
//   1 - ((ASPECT_RATIO_Y * OUTER_SLIDER_HEIGHT) / ASPECT_RATIO_X) * 100
// )
// LEFT_PERCENTAGE = 37.5
const CLASS_PIVOT_LEFT = 'left-0'
const CLASS_PIVOT_RIGHT = 'left-[37.5%]'
export interface IProps {
  id: string
  label?: string
  initialChecked: boolean
  isDisabled?: boolean
  isSmall?: boolean
  onToggle: (isChecked: boolean) => void
}

export const CustomSwitch = ({
  id,
  label,
  initialChecked,
  isDisabled,
  isSmall,
  onToggle
}: IProps) => {
  const [isChecked, setIsChecked] = useState(initialChecked)
  const [isBeingPressed, setIsBeingPressed] = useState(false)
  const [isBeingRelease, setIsBeingRelease] = useState(false)

  const handleChange = () => {
    if (isDisabled) {
      return
    }
    setIsChecked(!isChecked)
    onToggle(!isChecked)
  }

  return (
    <div className="flex items-center gap-2">
      <div
        className={composeClasses(
          CLASS_SWITCH_WRAPPER,
          isDisabled ? 'cursor-auto' : 'cursor-pointer',
          isSmall
            ? 'min-w-[3.4rem] max-w-[3.4rem]'
            : 'min-w-[4.2rem] max-w-[4.2rem]'
        )}
        onMouseDown={() => {
          setIsBeingRelease(false)
          setIsBeingPressed(true)
        }}
        onMouseUp={() => {
          setIsBeingPressed(false)
          setIsBeingRelease(true)
        }}
        onClick={handleChange}
      >
        <input
          id={id}
          type="checkbox"
          checked={isChecked}
          disabled={isDisabled}
          className="opacity-0 h-0 w-0 absolute"
          onChange={handleChange}
        />

        <span
          className={composeClasses(
            CLASS_SWITCH_BG,
            !isDisabled && isChecked ? 'bg-primary' : 'bg-surface-gray-20'
          )}
        />

        <span
          className={composeClasses(
            CLASS_SWITCH_OUTER_SLIDER,
            !isDisabled && 'group-hover:bg-notif-gray-01',
            isChecked ? CLASS_PIVOT_RIGHT : CLASS_PIVOT_LEFT
          )}
        >
          <span
            className={composeClasses(
              CLASS_ROUNDED_CENTER,
              'bg-[#d0d0d047] transition-all',
              !isDisabled && isBeingPressed
                ? 'h-full w-full'
                : CLASS_SWITCH_INNER_SIZE
            )}
          />

          <span
            className={composeClasses(
              CLASS_ROUNDED_CENTER,
              !isDisabled && isBeingRelease && 'switch-pulse'
            )}
          />
        </span>

        <span
          className={composeClasses(
            CLASS_SWITCH_OUTER_SLIDER,
            isChecked ? CLASS_PIVOT_RIGHT : CLASS_PIVOT_LEFT
          )}
        >
          <span
            className={composeClasses(
              'flex items-center justify-center',
              CLASS_SWITCH_INNER_SLIDER,
              isDisabled ? 'bg-secondary' : 'bg-white'
            )}
          >
            {isChecked ? (
              <GoogleIcon className="text-base" name="public" />
            ) : (
              <Image src={switchIcon} alt="Primera búsqueda" />
            )}
          </span>
        </span>
      </div>

      {label && label?.length > 0 ? (
        <label
          htmlFor={id}
          className={composeClasses(
            'select-none hidden md:block',
            isSmall ? 'text-body-m' : 'text-xs font-semibold',
            isDisabled
              ? 'cursor-auto text-secondary'
              : 'cursor-pointer text-primary'
          )}
        >
          {label}
        </label>
      ) : null}
    </div>
  )
}

export default CustomSwitch
